import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Send, Bot, Sparkles, Heart, Stethoscope, 
  FileText, Calendar, RefreshCw, Loader2, Award,
  BookOpen, Scale, AlertTriangle, CheckCircle, 
  Truck, Home, GraduationCap, Euro, Users,
  MessageSquare, History, Lightbulb, Volume2, Settings
} from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
import { SpeakButtonCompact } from '../components/SpeakButton';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icône cheval personnalisée
const HorseIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 8c-1.5 1-3.5 1-5 0-1.5-1-3.5-1-5 0s-3.5 1-5 0c-1.5-1-3.5-1-5 0"/>
    <path d="M5.5 18.5 2 22"/>
    <path d="m18.5 18.5 3.5 3.5"/>
    <path d="M12 22v-8"/>
    <path d="M8 22V10"/>
    <path d="M16 22V10"/>
    <path d="M12 14c-2-2-2-4 0-6s4 0 4 2"/>
    <path d="M12 8V2l2 2"/>
  </svg>
);

// Thèmes de conversation équins
const THEMES_EQUINS = [
  { 
    id: 'sante', 
    label: 'Santé & Vétérinaire', 
    icon: Stethoscope, 
    color: 'bg-red-500',
    description: 'Vaccins, vermifuges, maladies, soins courants',
    exemples: [
      "Quand dois-je faire vacciner mes chevaux ?",
      "Mon cheval boite, que faire ?",
      "Protocole de vermifugation recommandé ?"
    ]
  },
  { 
    id: 'alimentation', 
    label: 'Alimentation', 
    icon: BookOpen, 
    color: 'bg-green-500',
    description: 'Rations, foin, compléments, besoins nutritionnels',
    exemples: [
      "Quelle ration pour un cheval au travail ?",
      "Quantité de foin par jour ?",
      "Compléments pour jument gestante ?"
    ]
  },
  { 
    id: 'reglementation', 
    label: 'Réglementation', 
    icon: Scale, 
    color: 'bg-blue-500',
    description: 'SIRE, identification, normes, déclarations',
    exemples: [
      "Délai pour déclarer une naissance au SIRE ?",
      "Réglementation transport de chevaux ?",
      "Normes bien-être en pension ?"
    ]
  },
  { 
    id: 'debourrage', 
    label: 'Débourrage & Éducation', 
    icon: Award, 
    color: 'bg-amber-500',
    description: 'Techniques, étapes, problèmes comportementaux',
    exemples: [
      "À quel âge débourrer un cheval ?",
      "Mon poulain a peur du montoir, conseils ?",
      "Programme de débourrage en douceur ?"
    ]
  },
  { 
    id: 'attelage', 
    label: 'Attelage', 
    icon: Truck, 
    color: 'bg-stone-500',
    description: 'Harnachement, voitures, conduite, compétition',
    exemples: [
      "Quel harnais pour débuter l'attelage ?",
      "Réglementations concours d'attelage ?",
      "Entretien des voitures d'attelage ?"
    ]
  },
  { 
    id: 'reproduction', 
    label: 'Reproduction & Élevage', 
    icon: Heart, 
    color: 'bg-pink-500',
    description: 'Saillie, gestation, poulinage, génétique',
    exemples: [
      "Suivi de gestation recommandé ?",
      "Préparation au poulinage ?",
      "Choix de l'étalon, critères ?"
    ]
  },
  { 
    id: 'pension', 
    label: 'Gestion Pension', 
    icon: Home, 
    color: 'bg-indigo-500',
    description: 'Tarification, contrats, assurances, organisation',
    exemples: [
      "Tarif pension box conseillé ?",
      "Clauses essentielles contrat pension ?",
      "Gestion des impayés ?"
    ]
  },
  { 
    id: 'formation', 
    label: 'Formations & Certifications', 
    icon: GraduationCap, 
    color: 'bg-emerald-500',
    description: 'BPJEPS, galops, FFE, obligations moniteur',
    exemples: [
      "Comment devenir moniteur d'équitation ?",
      "Formations éthologie reconnues ?",
      "Passage des galops, organisation ?"
    ]
  }
];

// Suggestions rapides
const SUGGESTIONS_RAPIDES = [
  "Calendrier vaccinal annuel",
  "Ration type cheval au repos",
  "Délais déclaration SIRE",
  "Étapes débourrage 3 ans",
  "Tarif pension 2024",
  "Vermifuge printemps"
];

export default function BotEquinPage() {
  const { activeSociete } = useWorkspace();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Message d'accueil
  useEffect(() => {
    const welcomeMessage = {
      id: 'welcome',
      role: 'assistant',
      content: `🐴 **Bot Expert Équin activé !**

Bonjour ! Je suis votre assistant spécialisé dans le monde équin. Je peux vous aider sur :

• **Santé & Vétérinaire** - Vaccins, vermifuges, soins
• **Alimentation** - Rations, foin, compléments
• **Réglementation** - SIRE, identification, normes
• **Débourrage** - Techniques, comportement
• **Attelage** - Harnachement, compétition
• **Reproduction** - Saillie, gestation, poulinage
• **Gestion Pension** - Tarifs, contrats
• **Formations** - BPJEPS, galops, FFE

Posez-moi vos questions ou cliquez sur un thème ci-dessous !`,
      timestamp: new Date().toISOString()
    };
    setMessages([welcomeMessage]);
  }, []);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (content) => {
    if (!content.trim()) return;

    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/equin/bot/chat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Societe-Id': activeSociete?.id || ''
        },
        body: JSON.stringify({
          message: content,
          theme: selectedTheme,
          context: {
            societe_type: activeSociete?.type_exploitation,
            modules: activeSociete?.modules_actifs || []
          }
        })
      });

      if (response.ok) {
        const data = await response.json();
        const assistantMessage = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: data.response,
          sources: data.sources,
          suggestions: data.suggestions,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        toast.error('Erreur lors de la communication avec le bot');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputMessage);
    }
  };

  const selectTheme = (theme) => {
    setSelectedTheme(theme.id);
    sendMessage(`Je souhaite des informations sur : ${theme.label}`);
  };

  const handleSuggestion = (suggestion) => {
    sendMessage(suggestion);
  };

  const clearChat = () => {
    setMessages([{
      id: 'welcome-reset',
      role: 'assistant',
      content: '🐴 Conversation réinitialisée. Comment puis-je vous aider ?',
      timestamp: new Date().toISOString()
    }]);
    setSelectedTheme(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50" data-testid="bot-equin-page">
      <div className="container mx-auto p-4 max-w-6xl">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center">
                <HorseIcon className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-amber-900">Bot Expert Équin</h1>
                <p className="text-amber-700 text-sm">Assistant IA spécialisé filière équine</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                onClick={() => navigate('/agents/config', { state: { selectedAgent: 'equin' } })} 
                variant="outline" 
                size="sm"
                className="text-gray-600"
                data-testid="agent-settings-btn"
              >
                <Settings className="w-4 h-4 mr-2" /> Paramètres
              </Button>
              <Button onClick={clearChat} variant="outline" size="sm">
                <RefreshCw className="w-4 h-4 mr-2" /> Nouvelle conversation
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Sidebar Thèmes */}
          <div className="lg:col-span-1 space-y-4">
            <Card className="bg-white/80 backdrop-blur">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-amber-500" />
                  Thèmes
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {THEMES_EQUINS.map((theme) => {
                  const Icon = theme.icon;
                  const isSelected = selectedTheme === theme.id;
                  return (
                    <button
                      key={theme.id}
                      onClick={() => selectTheme(theme)}
                      className={`w-full flex items-center gap-2 p-2 rounded-lg text-left text-sm transition ${
                        isSelected 
                          ? 'bg-amber-100 text-amber-900 ring-2 ring-amber-400' 
                          : 'hover:bg-gray-100'
                      }`}
                    >
                      <div className={`w-6 h-6 ${theme.color} rounded flex items-center justify-center`}>
                        <Icon className="w-3 h-3 text-white" />
                      </div>
                      <span className="truncate">{theme.label}</span>
                    </button>
                  );
                })}
              </CardContent>
            </Card>

            {/* Suggestions rapides */}
            <Card className="bg-white/80 backdrop-blur">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  Suggestions
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                {SUGGESTIONS_RAPIDES.map((suggestion, idx) => (
                  <Badge
                    key={idx}
                    variant="outline"
                    className="cursor-pointer hover:bg-amber-50 transition"
                    onClick={() => handleSuggestion(suggestion)}
                  >
                    {suggestion}
                  </Badge>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Zone de chat principale */}
          <div className="lg:col-span-3">
            <Card className="bg-white/80 backdrop-blur h-[calc(100vh-200px)] flex flex-col">
              <CardContent className="flex-1 overflow-hidden p-0">
                {/* Messages */}
                <div className="h-full overflow-y-auto p-4 space-y-4">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-2xl p-4 ${
                          message.role === 'user'
                            ? 'bg-amber-500 text-white'
                            : 'bg-white border shadow-sm'
                        }`}
                      >
                        {message.role === 'assistant' && (
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center">
                                <Bot className="w-4 h-4 text-amber-600" />
                              </div>
                              <span className="text-xs text-gray-500">Bot Équin</span>
                            </div>
                            <SpeakButtonCompact text={message.content} voice="nova" />
                          </div>
                        )}
                        <div className="whitespace-pre-wrap text-sm">
                          {message.content}
                        </div>
                        {message.suggestions && message.suggestions.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {message.suggestions.map((suggestion, idx) => (
                              <Badge
                                key={idx}
                                variant="secondary"
                                className="cursor-pointer hover:bg-amber-100"
                                onClick={() => handleSuggestion(suggestion)}
                              >
                                {suggestion}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {loading && (
                    <div className="flex justify-start">
                      <div className="bg-white border rounded-2xl p-4 shadow-sm">
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
                          <span className="text-sm text-gray-500">Réflexion en cours...</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="border-t p-4 bg-gray-50">
                  <div className="flex gap-2">
                    <Textarea
                      ref={textareaRef}
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Posez votre question sur le monde équin..."
                      className="flex-1 min-h-[44px] max-h-[120px] resize-none"
                      rows={1}
                    />
                    <Button
                      onClick={() => sendMessage(inputMessage)}
                      disabled={loading || !inputMessage.trim()}
                      className="bg-amber-500 hover:bg-amber-600"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
